import { observable } from '@legendapp/state';
import type { TowersResultCacheDto } from '../api';
import { PATTERN_MAP } from '../constants';
import { gameState$ } from './game.state';

type BrickType = 'BRICK' | 'MINE' | 'UNKNOWN';

export type TowersBrick = {
  type: BrickType;
  isPicked: boolean;
};

type PickedBrick = {
  isBrick: boolean;
  isMine: boolean;
  column: number;
};

type TowersState = {
  bricks: TowersBrick[][];
  pickBrick: (result: PickedBrick) => void;
  setAllBricks: (results: TowersResultCacheDto[]) => void;
  resetBricks: () => void;
};

const getBrickType = (isBrick: boolean, isMine: boolean) => {
  if (isBrick) {
    return 'BRICK';
  }

  if (isMine) {
    return 'MINE';
  }

  return 'UNKNOWN';
};

export const towersState$ = observable<TowersState>({
  bricks: [],
  pickBrick: ({ column, isBrick, isMine }: PickedBrick) => {
    // generate full row results
    // based on existing results, get current row
    // based on pattern, generate X amount of results for that row
    // add them to the results
    // brick rows will be added from bottom to top

    const pattern = gameState$.pattern.peek();

    const { cols } = PATTERN_MAP[pattern];

    const pickedBrick: TowersBrick = {
      type: getBrickType(isBrick, isMine),
      isPicked: true,
    };

    const row: TowersBrick[] = [];

    for (let i = 0; i < cols; i++) {
      if (i === column) {
        row.push(pickedBrick);
      } else {
        row.push({ type: 'UNKNOWN', isPicked: false });
      }
    }

    towersState$.bricks.push(row);
  },
  setAllBricks: (results: TowersResultCacheDto[]) => {
    // Determine the current pattern to know the grid dimensions.
    const pattern = gameState$.pattern.peek();
    const patternConfig = PATTERN_MAP[pattern];

    // Handle invalid pattern
    if (!patternConfig) {
      console.error(`Invalid pattern: ${pattern}`);
      towersState$.bricks.set([]);
      return;
    }

    const { rows, cols } = patternConfig;

    const numberOfRowsInResults = results.length / cols;

    // Initialize the full grid with UNKNOWN bricks so that every row/column exists.
    const allBricks: TowersBrick[][] = Array.from(
      { length: numberOfRowsInResults },
      () =>
        Array.from({ length: cols }, () => ({
          type: 'UNKNOWN',
          isPicked: false,
        }))
    );

    // Populate the grid with the actual results coming from the backend.
    for (const { row, column, isBrick, isMine, isPicked } of results) {
      if (
        row === undefined ||
        column === undefined ||
        row < 0 ||
        row >= rows ||
        column < 0 ||
        column >= cols
      ) {
        continue;
      }

      const rowBricks = allBricks[row] ?? [];

      rowBricks[column] = {
        type: getBrickType(isBrick ?? false, isMine ?? false),
        isPicked,
      } as TowersBrick;
    }

    towersState$.bricks.set(allBricks);
  },
  resetBricks: () => {
    towersState$.bricks.set([]);
  },
});
